// composables/useAttendance.js
import { ref } from 'vue';
import axiosClient from '@/axiosClient';
import { API_ENDPOINTS } from '@/apiConfig';
import { useEmployeePhotos } from '@/composables/useEmployeePhotos';

export function useAttendance() {
  const { attachEmployeePhotos } = useEmployeePhotos();

  const allAttendance = ref([]);
  const attendanceLoading = ref(false);
  const attendanceError = ref(null);
  const attendanceWarning = ref(null);
  const attendancePagination = ref({
    page: 1,
    page_size: 100,
    total_items: 0,
    total_pages: 1
  });
  const attendanceSummary = ref({
    roster_duration_minutes: 0,
    worked_duration_minutes: 0,
    net_worked_duration_minutes: 0
  });

  const resetSearchMetadata = () => {
    attendancePagination.value = {
      page: 1,
      page_size: 100,
      total_items: 0,
      total_pages: 1
    };
    attendanceSummary.value = {
      roster_duration_minutes: 0,
      worked_duration_minutes: 0,
      net_worked_duration_minutes: 0
    };
  };

  const fetchAllAttendance = async (employeeId = 0, companyId = 0, startDate = null, endDate = null) => {
    attendanceLoading.value = true;
    attendanceError.value = null;
    attendanceWarning.value = null;

    try {
      const url = API_ENDPOINTS.fetchAttendance
        .replace('{employeeId}', employeeId)
        .replace('{companyId}', companyId)
        .replace('{startDate}', startDate || 'None')
        .replace('{endDate}', endDate || 'None');

      const response = await axiosClient.get(url);
      
      if (response.status !== 200) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      let modifiedResponse = response.data;

      // Check if the response is an array and not empty
      if (Array.isArray(modifiedResponse) && modifiedResponse.length > 0) {
        if (employeeId === 0 && companyId === 0) {
          modifiedResponse = modifiedResponse.map(entry => ({
            ...entry,
            shift_in2: entry.shift_in.substring(0, 16)
          }));
        }
        // Attach employee photos to the response data
        modifiedResponse = attachEmployeePhotos(modifiedResponse);
      } else {
        // If the response is empty or not an array, set it to an empty array
        modifiedResponse = [];
        // Set warning message
        attendanceWarning.value = response.data.message || 'No attendance records found';
      }

      allAttendance.value = modifiedResponse;
    } catch (err) {
      console.error('Error fetching attendance data:', err);
      attendanceError.value = err.response?.data?.message || err.message || 'Failed to fetch attendance data';
      allAttendance.value = [];
    } finally {
      attendanceLoading.value = false;
    }
  };

  const searchAttendance = async ({
    companyId = null,
    employeeIds,
    attendanceType,
    shiftType = 'ALL',
    showNotLogout = false,
    startDate,
    endDate,
    timezone = 'Europe/London',
    page = 1,
    pageSize = 100,
    sortField = 'ATTENDANCE_START',
    sortOrder = 'DESC'
  }) => {
    attendanceLoading.value = true;
    attendanceError.value = null;
    attendanceWarning.value = null;

    try {
      const response = await axiosClient.post(API_ENDPOINTS.searchAttendance, {
        company_id: companyId,
        employee_ids: employeeIds,
        status_filter: attendanceType,
        shift_type: shiftType,
        show_not_logout: showNotLogout,
        start_date: startDate,
        end_date: endDate,
        timezone,
        page,
        page_size: pageSize,
        sort_field: sortField,
        sort_order: sortOrder
      });
      const result = response.data;
      if (
        !result
        || !Array.isArray(result.items)
        || !result.pagination
        || !result.summary
        || !Number.isInteger(result.pagination.page)
        || !Number.isInteger(result.pagination.page_size)
        || !Number.isInteger(result.pagination.total_items)
        || !Number.isInteger(result.pagination.total_pages)
        || !Number.isInteger(result.summary.roster_duration_minutes)
        || !Number.isInteger(result.summary.worked_duration_minutes)
        || !Number.isInteger(result.summary.net_worked_duration_minutes)
      ) {
        throw new Error('Invalid attendance response: expected paginated data.');
      }

      allAttendance.value = attachEmployeePhotos(result.items);
      attendancePagination.value = result.pagination;
      attendanceSummary.value = result.summary;
      return true;
    } catch (err) {
      attendanceError.value = err.response?.data?.error
        || err.message
        || 'Failed to fetch attendance data';
      allAttendance.value = [];
      resetSearchMetadata();
      return false;
    } finally {
      attendanceLoading.value = false;
    }
  };

  return {
    allAttendance,
    attendanceLoading,
    attendanceError,
    attendanceWarning,
    attendancePagination,
    attendanceSummary,
    fetchAllAttendance,
    searchAttendance
  };
}
